/**
 * Service worker registration for MusicBox.
 *
 * The PWA install affordance (Chrome's "Install app" banner, iOS "Add to
 * Home Screen") is only useful to the HOST/DJ, never to a guest scanning
 * a QR code. We therefore:
 *
 *   1. Skip SW registration entirely on guest paths.
 *   2. Strip the <link rel="manifest"> so browsers don't surface the install
 *      prompt purely off the manifest's `display: standalone`.
 *   3. Unregister any previously-registered SW + clear its caches when a
 *      guest revisits — handles the case where the host installed it once
 *      then handed the device to a customer.
 *
 * On host routes, we register a network-first SW (see service-worker.js).
 *
 * ============== Cache-freshness contract (Feb 2026) ==============
 * Field-reported symptom: "changes not applying at client locations."
 * Root cause: venue tablets stay on the same tab for days/weeks, so the
 * browser never bothers to re-check for a new service-worker.js from
 * the network — the tab keeps running whichever build was cached the
 * first day the tablet booted.
 *
 * Three-layer fix:
 *   (a) `updateViaCache: 'none'` on register() — the browser is FORBIDDEN
 *       from serving service-worker.js from HTTP cache. Every navigation
 *       triggers a fresh SW file fetch, so a new deploy propagates within
 *       the browser's SW-file staleness heuristic (usually seconds).
 *   (b) `reg.update()` on load + every 30 minutes — proactively pokes the
 *       browser to check for a new SW file even when the tab isn't
 *       navigating (venue tablets = same tab for hours).
 *   (c) Weekly hard-reset — localStorage-timestamped force-purge of all
 *       caches + SW unregister + one reload. Guarantees no venue is
 *       running >7-day-old JS regardless of network flakiness.
 */

const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000;      // (b) poke for new SW every 30 min
// DAILY RESET (Oct 2026, per venue request): wipe caches ONCE per "venue
// day". The day boundary is 06:00 local time — so the reset fires at 6 AM
// if the app is running, or on the first load after 6 AM otherwise. A tab
// opened at 2 AM still belongs to the previous venue day and is NOT reset.
const AUTO_RESET_DAY_BOUNDARY_HOUR = 6;
const AUTO_RESET_FORCE_AFTER_MS = 3 * 3600 * 1000;   // still live 3h past 6 AM → force
const AUTO_RESET_LULL_WAIT_MS = 6 * 1000;    // how long to wait for a lull if audio is live
const AUTO_RESET_LULL_POLL_MS = 150;         // how often to poll the live flag while waiting
const AUTO_RESET_DEFER_INTERVAL_MS = 5 * 60 * 1000; // if audio is live, retry in 5 min
const AUTO_RESET_KEY = "spin:sw:lastCacheClearDay:v3";

// "Venue day" = local calendar date of (now − 6h), as YYYY-MM-DD.
function venueDayOf(ts) {
  const d = new Date(ts - AUTO_RESET_DAY_BOUNDARY_HOUR * 3600 * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
// ms since today's 06:00 boundary (how "overdue" the daily reset is).
function msSinceBoundary(ts) {
  const d = new Date(ts);
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate(), AUTO_RESET_DAY_BOUNDARY_HOUR, 0, 0, 0).getTime();
  return ts >= b ? ts - b : ts - (b - 24 * 3600 * 1000);
}

// ============== New-deploy reload gate (Sep 2026) ==============
// Field/RCA finding: the `controllerchange` reload below used to fire
// unconditionally the moment a new build activated — including mid-song,
// mid-set, with zero warning. Every deploy silently killed playback at
// every open venue tab. `performAutoCacheResetIfDue` above already solved
// this exact problem for the 12h/36h/40h periodic reset via
// `window.__spinItUpIsLive` + a lull-wait; we mirror that pattern here so
// a deploy landing mid-set doesn't cut the music. Timescale is compressed
// relative to the 12h/36h/40h tiers because this fires right after a
// deploy (not once a day) and a new build should still land promptly.
const SW_RELOAD_LULL_POLL_MS = 2000;           // how often to re-check liveness while deferred
const SW_RELOAD_FORCE_AFTER_MS = 30 * 60 * 1000; // safety net: reload anyway after 30 min so a
                                                  // venue that never has a lull isn't stuck on a
                                                  // stale build indefinitely

const GUEST_PATH_PATTERNS = [
  /^\/v\//,           // /v/:venueCode  → QR landing
  /^\/request\//,     // /request/:code → song-request page
  /^\/menu\//,
  /^\/review\//,
  /^\/play\//,
  /^\/tv\//,
  /\/tv$/,            // /:venueCode/tv
];

function isGuestPath() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname || "/";
  return GUEST_PATH_PATTERNS.some((re) => re.test(path));
}

function stripManifestLink() {
  if (typeof document === "undefined") return;
  document.querySelectorAll('link[rel="manifest"]').forEach((el) => el.parentNode && el.parentNode.removeChild(el));
  // Also drop apple-touch / theme-color tags that nudge "Add to Home Screen"
  // on iOS Safari. These are still useful for hosts — but on guest pages
  // they make customers think the venue is asking them to install something.
  document
    .querySelectorAll('meta[name="apple-mobile-web-app-capable"], meta[name="mobile-web-app-capable"]')
    .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
}

async function unregisterAllSWs() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
  } catch {
    /* noop */
  }
  // Clear cached app-shell + assets so the guest tab is a vanilla page load.
  if (typeof caches !== "undefined") {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    } catch {
      /* noop */
    }
  }
}

/**
 * Tiered auto-reset: after enough time has elapsed since the last purge,
 * wipe the SW cache + reload — but escalate the safety gate progressively
 * so a venue playing music non-stop still gets refreshed within ~1.7 days.
 *
 *   • 12h  — full idle only. If audio is live, defer 5 min.
 *   • 36h  — wait ~6s for ANY brief lull (crossfade gap, track transition).
 *            Fires as soon as `window.__spinItUpIsLive` reads false.
 *   • 40h  — unconditional. Clips at most a few seconds of the current
 *            track. Better than a sluggish 40h-old JS heap.
 *
 * The `__spinItUpIsLive` flag is set by MusicContext:
 *   window.__spinItUpIsLive = !!(isPlaying || crossfadeActive);
 *
 * Returns `true` if a reset was performed (caller should NOT continue
 * with normal SW registration — the reload will fire the whole flow
 * again with fresh state).
 */

async function _waitForBriefLull(maxWaitMs) {
  // Poll `window.__spinItUpIsLive` for a brief moment when it reads false.
  // Returns true if a lull was detected within `maxWaitMs`, false otherwise.
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    if (typeof window !== "undefined" && window.__spinItUpIsLive === false) {
      return true;
    }
    await new Promise((r) => setTimeout(r, AUTO_RESET_LULL_POLL_MS));
  }
  return false;
}

async function performAutoCacheResetIfDue() {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return false;
  const now = Date.now();
  const today = venueDayOf(now);
  let lastDay = "";
  try { lastDay = localStorage.getItem(AUTO_RESET_KEY) || ""; } catch { lastDay = ""; }
  // First-run visitors: stamp today but don't reload — no stale cache to purge yet.
  if (!lastDay) {
    try { localStorage.setItem(AUTO_RESET_KEY, today); } catch { /* noop */ }
    return false;
  }
  if (lastDay === today) return false;
  // Skip if the URL already has ?fresh= — prevents a reload loop if the
  // purge fails and the stamp doesn't update.
  if (window.location.search.includes("fresh=")) {
    try { localStorage.setItem(AUTO_RESET_KEY, today); } catch { /* noop */ }
    return false;
  }

  const isLive = window.__spinItUpIsLive === true;
  const forced = msSinceBoundary(now) >= AUTO_RESET_FORCE_AFTER_MS;

  if (isLive && !forced) {
    // Music is playing at 6 AM — wait up to 6s for a track-boundary lull;
    // otherwise defer to the next 5-min tick.
    const lull = await _waitForBriefLull(AUTO_RESET_LULL_WAIT_MS);
    if (!lull) {
      console.info("[SW] daily reset due but playback is live — deferring 5 min");
      return false;
    }
    console.info("[SW] daily reset — lull detected, wiping caches");
  } else if (isLive && forced) {
    console.info("[SW] daily reset 3h+ overdue — forcing, clipping current track");
  } else {
    console.info("[SW] daily 6 AM reset — wiping caches + reloading");
  }

  await unregisterAllSWs();
  try { localStorage.setItem(AUTO_RESET_KEY, today); } catch { /* noop */ }
  const sep = window.location.search ? "&" : "?";
  window.location.replace(`${window.location.pathname}${window.location.search}${sep}fresh=${now}${window.location.hash}`);
  return true;
}

// Fire-and-forget beacon into the same `playback_stalls` collection the
// Forensics dashboard already reads (see MusicContext.js `reportPlaybackStall`)
// so a deploy-triggered reload — deferred or forced — shows up as its own
// reason instead of looking like an unexplained heartbeat gap. Deliberately
// standalone (no import from MusicContext.js) since this file runs at boot,
// before React mounts, and must never depend on app state to function.
// Uses `sendBeacon` so the report survives the reload it's describing.
function reportSwReloadEvent(reason, meta = {}) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const url = `${base}/api/telemetry/playback-stall`;
    const payload = JSON.stringify({
      reason,
      meta,
      user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
      is_hidden: (typeof document !== "undefined" && document.hidden) || false,
      at: new Date().toISOString(),
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => { /* noop */ });
    }
  } catch {
    /* noop — telemetry must never block or break the reload flow */
  }
}

// Some browsers (Chrome desktop especially) fire a `beforeinstallprompt`
// event whenever the page meets installability criteria. We hard-suppress
// it on guest paths in case the manifest strip happens after install
// detection has already fired.
function blockInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
  });
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;

  if (isGuestPath()) {
    stripManifestLink();
    blockInstallPrompt();
    // Best-effort cleanup — don't block the page render on it.
    unregisterAllSWs();
    return;
  }

  if (!("serviceWorker" in navigator)) return;

  // (c) daily 6 AM auto-reset — must run BEFORE registration so we don't
  // register a SW just to immediately unregister it. If a reset fires,
  // the page reload will re-enter this whole function with fresh state.
  performAutoCacheResetIfDue().then((didReset) => {
    if (didReset) return; // reload in flight — no further work here

    // Periodic re-check: if the reset was DUE but deferred because audio
    // was live (or if we cross the 6 AM boundary during this session),
    // re-attempt on a 5-min interval. Cheap — mostly a no-op check.
    setInterval(() => {
      performAutoCacheResetIfDue().catch(() => { /* noop */ });
    }, AUTO_RESET_DEFER_INTERVAL_MS);

    // Defer registration until after the app paints so we don't block startup.
    window.addEventListener("load", () => {
      navigator.serviceWorker
        // `updateViaCache: 'none'` — (a) never serve service-worker.js from
        // HTTP cache. Ensures every SW-file fetch hits the network so new
        // deploys propagate immediately instead of waiting on browser's
        // ~24h SW-cache heuristic.
        .register("/service-worker.js", { scope: "/", updateViaCache: "none" })
        .then((reg) => {
          if (reg && reg.waiting) {
            reg.waiting.postMessage({ type: "SKIP_WAITING" });
          }
          reg.addEventListener("updatefound", () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener("statechange", () => {
              if (nw.state === "installed" && navigator.serviceWorker.controller) {
                nw.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });

          // (b) Proactively poke the browser to re-check for a new SW file
          // every 30 minutes. Handles the venue-tablet case where the tab
          // stays open for days — browser's own SW-update heuristic can
          // otherwise miss deploys entirely.
          setInterval(() => {
            reg.update().catch(() => { /* offline — try again next tick */ });
          }, SW_UPDATE_INTERVAL_MS);

          // Also poke immediately on tab-focus so a DJ tabbing back to the
          // console after checking Instagram picks up the new build.
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") {
              reg.update().catch(() => { /* noop */ });
            }
          });
        })
        .catch((err) => {
          console.warn("[SW] registration failed:", err);
        });

      // Auto-reload so users get the new build instead of running stale
      // cached JS (which was causing "Video unavailable — skipping" toasts
      // from the deprecated YouTube engine) — but never yank a live
      // performance out from under a host. Gated on `window.__spinItUpIsLive`
      // (same flag `performAutoCacheResetIfDue` above already trusts): if a
      // venue is silent, reload now; if it's live, wait for the next natural
      // lull (track change / crossfade gap / pause) and reload then. A
      // bounded safety timer forces the reload anyway after
      // `SW_RELOAD_FORCE_AFTER_MS` so a venue that never has a lull (fully
      // continuous back-to-back sets) doesn't run a stale build forever.
      let reloaded = false;
      let reloadWaitTimer = null;

      function doGatedReload(reason) {
        if (reloaded) return;
        reloaded = true;
        if (reloadWaitTimer) {
          clearInterval(reloadWaitTimer);
          reloadWaitTimer = null;
        }
        // Oct 3 2026 — never let SW-driven reloads chain faster than 1/hour
        // (protects against pods serving differing SW bytes mid-rollout).
        try {
          const last = Number(window.localStorage.getItem("spin.swReloadAt") || 0);
          if (Date.now() - last < 60 * 60 * 1000) {
            reportSwReloadEvent(reason + "_suppressed");
            return;
          }
          window.localStorage.setItem("spin.swReloadAt", String(Date.now()));
        } catch { /* noop */ }
        reportSwReloadEvent(reason);
        window.location.reload();
      }

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (reloaded || reloadWaitTimer) return; // already reloaded, or already waiting for a lull

        const isLive = window.__spinItUpIsLive === true;
        if (!isLive) {
          doGatedReload("sw_update_reload");
          return;
        }

        console.info("[SW] new build activated but playback is live — deferring reload for a lull");
        reportSwReloadEvent("sw_update_reload_deferred");
        const waitStart = Date.now();
        reloadWaitTimer = setInterval(() => {
          const stillLive = window.__spinItUpIsLive === true;
          const waited = Date.now() - waitStart;
          if (!stillLive) {
            doGatedReload("sw_update_reload_after_lull");
          } else if (waited >= SW_RELOAD_FORCE_AFTER_MS) {
            console.info("[SW] no lull after", Math.round(waited / 60000), "min — forcing reload");
            doGatedReload("sw_update_reload_forced");
          }
        }, SW_RELOAD_LULL_POLL_MS);
      });
    });
  });
}

/**
 * Public helper — venue admin can trigger a full cache wipe on demand.
 * Wired to the "Reset app cache" button in Settings so on-site staff can
 * force-refresh without waiting for the 12h timer.
 */
export async function forceResetAppCache() {
  await unregisterAllSWs();
  try { localStorage.setItem(AUTO_RESET_KEY, String(Date.now())); } catch { /* noop */ }
  const sep = window.location.search ? "&" : "?";
  window.location.replace(`${window.location.pathname}${window.location.search}${sep}fresh=${Date.now()}${window.location.hash}`);
}
